import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal, MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { MarketPrice, StoreVariantPricing } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useMarkets } from '@/features/markets';

import {
  useMarketPrices,
  useRemoveMarketPrice,
  useSetMarketPrices,
} from '../hooks/use-market-prices';
import {
  draftFromEntry,
  isMarketDraftDirty,
  refusedVariantId,
  toMarketPriceItems,
  type MarketPriceDraft,
} from '../schemas/market-price-rules';

export interface MarketPricesModalProps {
  storeId: string;
  storeProductId: string;
  productId: string;
  /** The variants of the product, as the store price grid lists them. */
  variants: readonly StoreVariantPricing[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function OpenModal({
  storeId,
  storeProductId,
  productId,
  variants,
  onClose,
}: Omit<MarketPricesModalProps, 'open' | 'onOpenChange'> & { onClose: () => void }) {
  const { t } = useTranslation(['storeCatalog', 'common', 'errors']);
  const format = useFormat();
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const markets = useMarkets(storeId);
  const [picked, setPicked] = useState<string | null>(null);
  const list = markets.data ?? [];
  const market = list.find((m) => m.id === picked) ?? list.find((m) => !m.isDefault) ?? list[0];
  const marketId = market?.id ?? null;
  const currency = market?.currency ?? '';

  const query = useMarketPrices(storeId, marketId, productId);
  const save = useSetMarketPrices(storeId, storeProductId, productId);
  const remove = useRemoveMarketPrice(storeId, storeProductId, productId);
  const [drafts, setDrafts] = useState<Map<string, MarketPriceDraft>>(new Map());
  const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map());
  const [removingId, setRemovingId] = useState<string | null>(null);

  const entries = new Map<string, MarketPrice>(
    (query.data?.items ?? []).map((entry) => [entry.variantId, entry]),
  );
  const draftFor = (variantId: string) =>
    drafts.get(variantId) ?? draftFromEntry(entries.get(variantId));
  const items = toMarketPriceItems(drafts, entries);

  const pickMarket = (id: string) => {
    setPicked(id);
    // Drafts are in the market's own currency.
    setDrafts(new Map());
    setRowErrors(new Map());
    save.reset();
  };

  const setDraft = (variantId: string, draft: MarketPriceDraft) => {
    setDrafts((current) => new Map(current).set(variantId, draft));
    setRowErrors((current) => {
      if (!current.has(variantId)) return current;
      const next = new Map(current);
      next.delete(variantId);
      return next;
    });
  };

  const forget = (variantIds: readonly string[]) =>
    setDrafts((current) => {
      const next = new Map(current);
      for (const id of variantIds) next.delete(id);
      return next;
    });

  const saveAll = () => {
    if (items.length === 0 || !marketId) return;
    save.mutate(
      { marketId, body: { items } },
      {
        onSuccess: () => forget(items.map((item) => item.variantId)),
        onError: (error) => {
          if (!isApiError(error)) return;
          const refused = error.details.flatMap((detail) => {
            const variantId = refusedVariantId(detail.field, items);
            return variantId ? [[variantId, detail.message] as const] : [];
          });
          if (refused.length > 0) setRowErrors(new Map(refused));
        },
      },
    );
  };

  const removeOne = (variantId: string) => {
    if (!marketId) return;
    setRemovingId(variantId);
    remove.mutate(
      { marketId, variantId },
      {
        onSuccess: () => forget([variantId]),
        onError: (error) =>
          setRowErrors((current) => new Map(current).set(variantId, message(error))),
        onSettled: () => setRemovingId(null),
      },
    );
  };

  return (
    <EditModal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      title={t('storeCatalog:marketPrices.title')}
      description={t('storeCatalog:marketPrices.description')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          {canWrite && market ? (
            <Button type="button" disabled={items.length === 0 || save.isPending} onClick={saveAll}>
              {save.isPending
                ? t('common:actions.saving')
                : t('storeCatalog:marketPrices.save', { count: items.length })}
            </Button>
          ) : null}
        </>
      }
    >
      {markets.isPending ? (
        <Skeleton className="h-9 w-60" />
      ) : markets.error ? (
        <ErrorState error={markets.error} onRetry={() => void markets.refetch()} compact />
      ) : !market ? (
        <p className="text-muted-foreground text-sm">{t('storeCatalog:marketPrices.noMarkets')}</p>
      ) : (
        <>
          <div className="grid gap-1.5">
            <Label htmlFor="market-prices-market">{t('storeCatalog:marketPrices.market')}</Label>
            <Select value={market.id} onValueChange={pickMarket}>
              <SelectTrigger id="market-prices-market" className="w-full sm:w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {list.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {t('storeCatalog:marketPrices.marketOption', {
                      name: m.name,
                      currency: m.currency,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {query.isPending ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-9" />
              ))}
            </div>
          ) : query.error ? (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
          ) : (
            <div className="flex flex-col gap-3">
              {save.isError && rowErrors.size === 0 ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>
                    {message(save.error)}
                    {isApiError(save.error) && save.error.requestId ? (
                      <span className="block text-xs">
                        {t('errors:requestIdShort', { requestId: save.error.requestId })}
                      </span>
                    ) : null}
                  </AlertDescription>
                </Alert>
              ) : null}
              <Table aria-label={t('storeCatalog:marketPrices.title')}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('storeCatalog:pricing.columns.variant')}</TableHead>
                    <TableHead>
                      {t('storeCatalog:marketPrices.columns.price', { currency })}
                    </TableHead>
                    <TableHead>{t('storeCatalog:pricing.columns.compareAt')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variants.map((variant) => {
                    const title =
                      variant.title !== '' ? variant.title : (variant.sku ?? variant.variantId);
                    const entry = entries.get(variant.variantId);
                    const draft = draftFor(variant.variantId);
                    const error = rowErrors.get(variant.variantId);
                    return (
                      <TableRow
                        key={variant.variantId}
                        className={
                          isMarketDraftDirty(draft, entry) ? 'bg-primary-tint/20' : undefined
                        }
                      >
                        <TableCell className="min-w-36">
                          <span className="block font-medium">{title}</span>
                          <span className="text-muted-foreground block text-xs">
                            {entry
                              ? t('storeCatalog:marketPrices.fixedSince', {
                                  when: format.relative(entry.updatedAt),
                                })
                              : t('storeCatalog:marketPrices.converted')}
                          </span>
                          {error ? (
                            <span role="alert" className="text-destructive block text-xs">
                              {error}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="w-36">
                          <MoneyInput
                            className="h-8"
                            currency={currency}
                            value={draft.price}
                            disabled={!canWrite}
                            placeholder={t('storeCatalog:marketPrices.notFixed')}
                            aria-label={t('storeCatalog:marketPrices.priceFor', { variant: title })}
                            onChange={(price) => setDraft(variant.variantId, { ...draft, price })}
                          />
                        </TableCell>
                        <TableCell className="w-36">
                          <MoneyInput
                            className="h-8"
                            currency={currency}
                            value={draft.compareAt}
                            disabled={!canWrite || !draft.price}
                            aria-label={t('storeCatalog:pricing.columns.compareAtFor', {
                              variant: title,
                            })}
                            onChange={(compareAt) =>
                              setDraft(variant.variantId, { ...draft, compareAt })
                            }
                          />
                        </TableCell>
                        <TableCell className="w-24 text-right">
                          {entry && canWrite ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={removingId === variant.variantId || save.isPending}
                              aria-label={t('storeCatalog:marketPrices.removeFor', {
                                variant: title,
                              })}
                              onClick={() => removeOne(variant.variantId)}
                            >
                              {t('storeCatalog:marketPrices.remove')}
                            </Button>
                          ) : entry ? (
                            <Badge variant="outline">{t('storeCatalog:marketPrices.fixed')}</Badge>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </EditModal>
  );
}

/**
 * Fixed prices per market for every variant (doc 04): a fixed price wins over the store price and
 * the converted base. Entered in the market's own currency. Acts at once, off the save bar.
 */
export function MarketPricesModal({ open, onOpenChange, ...props }: MarketPricesModalProps) {
  return open ? <OpenModal {...props} onClose={() => onOpenChange(false)} /> : null;
}
